(() => {
  if (window.KPRFavorites) {
    window.KPRFavorites.init();
    return;
  }

  const storageKey = 'kpr:favorites:v1';
  const maximum = 50;
  const validHandle = (handle) => typeof handle === 'string' && /^[\p{L}\p{N}][\p{L}\p{N}-]{0,254}$/u.test(handle);
  const pageStates = new WeakMap();
  const select = (root, selector) => root.querySelector(selector);
  const selectAll = (root, selector) => [...root.querySelectorAll(selector)];

  const readSaved = () => {
    let raw;
    try {
      raw = window.localStorage.getItem(storageKey);
    } catch {
      return {ok: false, reason: 'storage_error'};
    }
    if (raw === null) return {ok: true, handles: []};
    try {
      if (raw.length > 16384) throw new Error();
      const value = JSON.parse(raw);
      if (!Array.isArray(value) || value.length > maximum || !value.every(validHandle)) throw new Error();
      return {ok: true, handles: [...new Set(value)]};
    } catch {
      return {ok: false, reason: 'corrupt_error'};
    }
  };

  const writeSaved = (handles) => {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(handles));
      return true;
    } catch {
      return false;
    }
  };

  const scopeMessage = (scope, key) => {
    const message = select(scope, '[data-kpr-favorite-message]');
    if (message) message.textContent = scope.dataset[key] || '';
  };

  const syncButtons = (state) => {
    selectAll(document, '[data-kpr-favorite]').forEach((button) => {
      button.disabled = !validHandle(button.dataset.kprFavorite);
      const scope = button.closest('[data-kpr-favorite-scope]');
      if (!state.ok) {
        if (scope) scopeMessage(scope, state.reason === 'corrupt_error' ? 'corruptError' : 'storageError');
        return;
      }
      const saved = state.handles.includes(button.dataset.kprFavorite);
      button.setAttribute('aria-pressed', String(saved));
      button.setAttribute('aria-label', saved ? button.dataset.removeLabel : button.dataset.addLabel);
      const label = select(button, '[data-kpr-favorite-text]');
      if (label) label.textContent = saved ? button.dataset.removeText : button.dataset.addText;
      const message = scope && select(scope, '[data-kpr-favorite-message]');
      if (message) message.textContent = '';
    });
  };

  const labelsFor = (page) => JSON.parse(select(page, '[data-kpr-favorites-labels]').textContent);
  const showPageError = (page, message) => {
    const error = select(page, '[data-kpr-favorites-error]');
    error.textContent = message;
    error.hidden = !message;
  };

  const rootUrl = (page) => {
    const candidate = window.Shopify?.routes?.root || page.dataset.rootUrl || '/';
    try {
      const url = new URL(candidate, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error();
      return url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
    } catch {
      return '/';
    }
  };

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };

  const safeImage = (source) => {
    if (typeof source !== 'string') return null;
    try {
      const url = new URL(source, window.location.origin);
      if (url.protocol !== 'https:') return null;
      if (url.origin !== window.location.origin && url.hostname !== 'cdn.shopify.com' && !url.hostname.endsWith('.shopify.com')) return null;
      url.searchParams.set('width', '640');
      return url.href;
    } catch {
      return null;
    }
  };

  const fillProduct = (item, product, handle, page, labels) => {
    const url = `${rootUrl(page)}products/${encodeURIComponent(handle)}`;
    const imageUrl = safeImage(product.featured_image);
    if (imageUrl) {
      const imageLink = element('a');
      imageLink.href = url;
      imageLink.setAttribute('aria-label', product.title);
      const image = element('img', 'kpr-favorites__image');
      image.src = imageUrl;
      image.alt = product.title;
      image.width = 640;
      image.height = 640;
      image.loading = 'lazy';
      image.addEventListener('error', () => imageLink.remove(), {once: true});
      imageLink.append(image);
      item.prepend(imageLink);
    }
    const heading = select(item, 'h2');
    const titleLink = element('a', '', product.title);
    titleLink.href = url;
    heading.replaceChildren(titleLink);
    select(item, '[data-kpr-favorites-product-state]').textContent = product.available ? labels.available : labels.unavailable;
    if (typeof product.price === 'number' && Number.isFinite(product.price) && product.price >= 0) {
      try {
        const locale = page.dataset.currency === 'CAD' ? 'en-US' : (document.documentElement.lang || 'en');
        const price = new Intl.NumberFormat(locale, {style: 'currency', currency: page.dataset.currency}).format(product.price / 100);
        const priceText = product.price_varies ? labels.from_price.replace('__PRICE__', price) : price;
        heading.after(element('p', 'kpr-favorites__price', priceText));
      } catch {}
    }
    const link = element('a', 'kpr-favorites__view', labels.view);
    link.href = url;
    const remove = select(item, '[data-kpr-favorite-remove]');
    remove.setAttribute('aria-label', labels.remove_label.replace('__PRODUCT__', product.title));
    remove.before(link);
  };

  const renderPage = async (page, state) => {
    const session = pageStates.get(page);
    if (!session) return;
    const version = ++session.version;
    session.controllers.forEach((controller) => controller.abort());
    session.controllers.clear();
    const labels = labelsFor(page);
    const list = select(page, '[data-kpr-favorites-list]');
    const status = select(page, '[data-kpr-favorites-status]');
    const empty = select(page, '[data-kpr-favorites-empty]');
    const retry = select(page, '[data-kpr-favorites-retry]');
    const reset = select(page, '[data-kpr-favorites-reset]');
    list.replaceChildren();
    list.setAttribute('aria-busy', 'false');
    retry.hidden = true;
    reset.hidden = true;
    empty.hidden = true;
    showPageError(page, '');
    if (!state.ok) {
      status.textContent = '';
      showPageError(page, labels[state.reason]);
      retry.hidden = false;
      reset.hidden = state.reason !== 'corrupt_error';
      return;
    }
    const handles = state.handles;
    empty.hidden = handles.length !== 0;
    status.textContent = labels.count.replace('__COUNT__', String(handles.length));
    if (!handles.length) return;
    list.setAttribute('aria-busy', 'true');
    const items = handles.map((handle) => {
      const item = element('li', 'kpr-favorites__item');
      item.append(element('h2', '', handle));
      const availability = element('p', '', labels.loading);
      availability.setAttribute('data-kpr-favorites-product-state', '');
      const remove = element('button', '', labels.remove);
      remove.type = 'button';
      remove.setAttribute('data-kpr-favorite-remove', handle);
      remove.setAttribute('aria-label', labels.remove_label.replace('__PRODUCT__', handle));
      item.append(availability, remove);
      list.append(item);
      return item;
    });
    let nextIndex = 0;
    let failed = 0;
    const worker = async () => {
      while (nextIndex < handles.length && session.version === version) {
        const index = nextIndex++;
        const controller = new AbortController();
        session.controllers.add(controller);
        const timer = window.setTimeout(() => controller.abort(), 12000);
        try {
          const response = await fetch(`${rootUrl(page)}products/${encodeURIComponent(handles[index])}.js`, {signal: controller.signal, credentials: 'same-origin', headers: {Accept: 'application/json'}});
          if (!response.ok) throw new Error();
          const product = await response.json();
          if (!product || typeof product.title !== 'string' || typeof product.available !== 'boolean') throw new Error();
          if (session.version !== version) return;
          fillProduct(items[index], product, handles[index], page, labels);
        } catch {
          if (session.version !== version) return;
          failed += 1;
          select(items[index], '[data-kpr-favorites-product-state]').textContent = labels.product_error;
        } finally {
          window.clearTimeout(timer);
          session.controllers.delete(controller);
        }
      }
    };
    await Promise.all(Array.from({length: Math.min(4, handles.length)}, worker));
    if (session.version !== version) return;
    list.setAttribute('aria-busy', 'false');
    retry.hidden = failed === 0;
    if (failed) showPageError(page, labels.partial_error);
  };

  const refresh = () => {
    const state = readSaved();
    syncButtons(state);
    selectAll(document, '[data-kpr-favorites-page]').forEach((page) => renderPage(page, state));
  };

  const init = () => {
    const state = readSaved();
    syncButtons(state);
    selectAll(document, '[data-kpr-favorites-page]').forEach((page) => {
      if (pageStates.has(page)) return;
      pageStates.set(page, {version: 0, controllers: new Set()});
      renderPage(page, state);
    });
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-kpr-favorite], [data-kpr-favorite-remove], [data-kpr-favorites-retry], [data-kpr-favorites-reset]');
    if (!button) return;
    event.preventDefault();
    const page = button.closest('[data-kpr-favorites-page]');
    if (button.hasAttribute('data-kpr-favorites-retry')) {
      refresh();
      return;
    }
    if (button.hasAttribute('data-kpr-favorites-reset')) {
      if (writeSaved([])) refresh();
      else showPageError(page, labelsFor(page).storage_error);
      return;
    }
    const scope = button.closest('[data-kpr-favorite-scope]');
    const state = readSaved();
    if (!state.ok) {
      syncButtons(state);
      if (page) renderPage(page, state);
      return;
    }
    const removeOnly = button.hasAttribute('data-kpr-favorite-remove');
    const handle = removeOnly ? button.dataset.kprFavoriteRemove : button.dataset.kprFavorite;
    if (!validHandle(handle)) return;
    const removing = removeOnly || state.handles.includes(handle);
    if (!removing && state.handles.length >= maximum) {
      if (scope) scopeMessage(scope, 'limitError');
      return;
    }
    const next = removing ? state.handles.filter((item) => item !== handle) : [...state.handles, handle];
    if (!writeSaved(next)) {
      if (scope) scopeMessage(scope, 'storageError');
      if (page) showPageError(page, labelsFor(page).storage_error);
      return;
    }
    refresh();
    if (scope) scopeMessage(scope, removing ? 'removedMessage' : 'savedMessage');
    if (removeOnly && page) {
      const focusTarget = select(page, '[data-kpr-favorite-remove]') || select(page, '[data-kpr-favorites-empty] h2');
      focusTarget?.focus();
    }
  });
  window.addEventListener('storage', (event) => {
    if (event.key === storageKey || event.key === null) refresh();
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
    else init();
  });
  document.addEventListener('shopify:section:load', init);
  window.KPRFavorites = Object.freeze({init});
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once: true});
  else init();
  if ('MutationObserver' in window) {
    new MutationObserver((records) => {
      if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches('[data-kpr-favorite]') || node.querySelector('[data-kpr-favorite]'))))) syncButtons(readSaved());
    }).observe(document.documentElement, {subtree: true, childList: true});
  }
})();
